<template>
  <div class="v2-video-item" :class="theme">
    <div class="ratio ratio-16x9">
      <div class="video">
        <div v-if="!playable" class="thumb playable" @click.prevent="signupButtonClick('play video in categories')">
          <img v-lazy="thumbnail" :alt="`${title} video thumbnail`" />
        </div>
        <player
          v-else
          :id="video.id"
          :source="video.hls_url as string"
          :subtitles-de="video.subtitles_de || undefined"
          :subtitles-fr="video.subtitles_fr || undefined"
          :subtitles-en="video.subtitles_de || undefined"
          :poster="thumbnail"
          ratio="16x9"
        />
        <like-button @click="signupButtonClick('like click')" />
      </div>
    </div>
    <div class="title">
      {{ title }}
    </div>
    <rating-badge v-if="showRating" :rating="video.rating" />
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, PropType } from "vue";
import { useModalStore } from "@/store/modal";
import { useI18n } from "vue-i18n";
import { getPublicVideoPoster, getVideoPoster } from "@/utils/mediaHelper";
import LikeButton from "@/components/v2/like-button/index.vue";
import { amplitudeButtonClickTrack } from "@/utils/amplitude";
import { Video } from "@/interfaces/Video/Video.ts";
import { isPerformanceCheck } from "@/utils/domHelper.ts";
import RatingBadge from "@/components/v2/rating-badge/index.vue";

defineOptions({
  name: "video-item",
});

const props = defineProps({
  video: {
    type: Object as PropType<Video>,
    require: true,
    default: null,
  },
  playable: {
    type: Boolean,
    require: false,
    default: false,
  },
  theme: {
    type: String,
    required: false,
    default: "dark",
    validator: (value: string) => {
      return ["light", "dark"].includes(value);
    },
  },
  showRating: {
    type: Boolean,
    require: false,
    default: true,
  },
});

const Player = isPerformanceCheck ? null : defineAsyncComponent(() => import("@/components/player/index.vue"));

const modalStore = useModalStore();

const { locale } = useI18n();

const title = computed(() => {
  return props.video[`public_title_${locale.value}`] || props.video[`title_${locale.value}`];
});

const videoUrl = computed(() => {
  return `/shoot/${props.video.gallery.id}#video-${props.video.id}`;
});

const thumbnail = computed(() => {
  if (props.video.thumbnail_public) {
    return getPublicVideoPoster(props.video.thumbnail_public);
  }

  let thumbName = "";

  for (const i in props.video.thumbnails) {
    const thumb = props.video.thumbnails[i];

    if (thumb.is_main) {
      thumbName = thumb.file_name;
      break;
    }
  }

  return thumbName ? getVideoPoster(thumbName) : "";
});

const signupButtonClick = (trackName: string) => {
  modalStore.showModal({ id: "modal-signup", redirectUrl: videoUrl.value });
  amplitudeButtonClickTrack(trackName, props.video.id);
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
